import usePagination from "src/customHooks/usePagination";
import { useGetUsersTransactionsQuery, useCountUsersTransactionsQuery } from "../user-transactions.service";
import PageHeader from "src/components/pageHeader";
import { UserTransaction } from "../types/user-transaction.entity";
import { UsersTransactionsTable } from "../components/transactions-table";
import { UsersTransactionsTableSkeleton } from "../components/transactions-table/UsersTransactionsTableSkeleton";
import Pagination from "src/components/pagination";
import { Navigate } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import { UsersTransactionsRouteState, UsersTransactionsSearch } from "../components/transactions-table/search";
import { useCallback } from "react";
import { UsersTransactionsFilter } from "../types";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

export const UsersTransactions = () => {
  const tableSearch = usePaginatedTableSearchParams<UsersTransactionsRouteState>({
    parseState: (searchParams) => {
      const query: UsersTransactionsRouteState = {};
      const user = searchParams.get("user");
      const bookingId = searchParams.get("bookingId");
      const sessionId = searchParams.get("sessionId");
      const status = searchParams.get("status");
      const type = searchParams.get("type");
      const currency = searchParams.get("currency");

      if (user) query.user = user;
      if (bookingId) query.bookingId = bookingId;
      if (sessionId) query.sessionId = sessionId;
      if (status) query.status = status as UsersTransactionsRouteState["status"];
      if (type) query.type = type as UsersTransactionsRouteState["type"];
      if (currency) query.currency = currency as UsersTransactionsRouteState["currency"];

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.user) nextParams.set("user", state.user);
      if (state.bookingId) nextParams.set("bookingId", state.bookingId);
      if (state.sessionId) nextParams.set("sessionId", state.sessionId);
      if (state.status) nextParams.set("status", state.status);
      if (state.type) nextParams.set("type", state.type);
      if (state.currency) nextParams.set("currency", state.currency);
      return nextParams;
    },
  });

  const routeState = tableSearch.state;
  const initialQuery: UsersTransactionsFilter = {
    ...(routeState.user ? { userIdentifier: routeState.user } : {}),
    ...(routeState.bookingId ? { bookingId: routeState.bookingId } : {}),
    ...(routeState.sessionId ? { sessionId: routeState.sessionId } : {}),
    ...(routeState.status ? { status: routeState.status } : {}),
    ...(routeState.type ? { type: routeState.type } : {}),
    ...(routeState.currency ? { currency: routeState.currency } : {}),
  };

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } =
    usePagination<UserTransaction>({
      useGetDataQuery: useGetUsersTransactionsQuery,
      useCountData: useCountUsersTransactionsQuery,
      initQuery: initialQuery,
      initPageTokens: tableSearch.pageTokens,
      initCurrentIndex: tableSearch.currentIndex,
      syncKey: tableSearch.syncKey,
      onPaginationStateChange: tableSearch.replacePagination,
    });

  const updateQuery = useCallback(
    (filter: UsersTransactionsRouteState) => {
      tableSearch.replaceFilters(filter);
    },
    [tableSearch]
  );

  const showTableSkeleton = !data && (isLoading || isFetching);

  return (
    <div className="page_container">
      <PageHeader text="Users Transactions" />

      <UsersTransactionsSearch updateQuery={updateQuery} initialQuery={routeState} />

      {isSuccess ? (
        <>
          {showTableSkeleton ? (
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
                <thead className="bg-gray-800 text-white text-center">
                  <tr>
                    <th className="px-4 py-3">Id</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Reason</th>
                    <th className="px-4 py-3">View</th>
                  </tr>
                </thead>
                <tbody>
                  <UsersTransactionsTableSkeleton rows={10} />
                </tbody>
              </table>
            </div>
          ) : (
            <UsersTransactionsTable data={data ?? []} />
          )}
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <Navigate to="/505" />
      ) : (
        <PageLoading />
      )}
    </div>
  );
};
